<template>
  <div class="page-translator-container">
    <div class="translator-header">
      <div class="language-selector" :class="statusClass">
        <div class="custom-language-selector">
          <button
            class="language-button"
            @click="toggleLanguageMenu"
            :aria-label="`Current language: ${selectedLanguage === 'fr' ? 'French' : 'English'}`"
          >
            <span class="flag-icon" v-html="getFlagEmoji(selectedLanguage)"></span>
            <span class="language-code">{{ selectedLanguage.toUpperCase() }}</span>
          </button>
          
          <div v-if="showLanguageMenu" class="language-menu">
            <button
              v-for="lang in languages"
              :key="lang.code"
              class="language-option"
              :class="{ active: selectedLanguage === lang.code }"
              @click="selectLanguage(lang.code)"
              :aria-label="`Select ${lang.name}`"
            >
              <span class="flag-icon" v-html="lang.flag"></span>
              <span class="language-name">{{ lang.name }}</span>
            </button>
          </div>
        </div>
        
        <div v-if="isTranslating" class="translation-status">
          <span class="spinner"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "/node_modules/flag-icons/css/flag-icons.min.css";
export default {
  name: 'PageTranslator',
  data() {
    return {
      selectedLanguage: 'fr',
      isTranslating: false,
      translationStatus: null,
      originalPageContent: [],
      originalPlaceholders: {}, // Store original placeholders
      showLanguageMenu: false,
      observer: null, // For MutationObserver
      isObserverSuspended: false,
      observerOptions: {
        childList: true,
        subtree: true,
        characterData: true,
        characterDataOldValue: true
      },
      languages: [
        { code: 'fr', name: 'FR', flag: '<span style="width: 20px; height: 13px;" class="fi fi-fr"></span>' },
        { code: 'en', name: 'EN', flag: '<span style="width: 20px; height: 13px;" class="fi fi-gb"></span>' }
      ]
    };
  },
  computed: {
    statusClass() {
      if (this.translationStatus === 'success') return 'status-success';
      if (this.translationStatus === 'error') return 'status-error';
      return '';
    }
  },
  async mounted() {
    // Wait for the page to be completely loaded before storing content
    await this.waitForPageLoad();
    
    const savedLanguage = localStorage.getItem('pageLanguage');
    if (savedLanguage) {
      this.selectedLanguage = savedLanguage;
      this.storeOriginalContent();
      this.storeOriginalPlaceholders();
      if (this.selectedLanguage !== 'fr') {
        await this.translateFullPage();
      }
    } else {
      this.storeOriginalContent();
      this.storeOriginalPlaceholders();
    }
    this.setupMutationObserver();
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    async waitForPageLoad() {
      // Wait for document to be fully loaded
      if (document.readyState === 'loading') {
        await new Promise(resolve => {
          document.addEventListener('DOMContentLoaded', resolve, { once: true });
        });
      }
      
      // Additional wait for images and other resources to load
      if (document.readyState !== 'complete') {
        await new Promise(resolve => {
          window.addEventListener('load', resolve, { once: true });
        });
      }
      
      // Give the page a small buffer to ensure all dynamic content is rendered
      await new Promise(resolve => setTimeout(resolve, 100));
    },

    toggleLanguageMenu() {
      this.showLanguageMenu = !this.showLanguageMenu;
    },

    async selectLanguage(languageCode) {
      this.selectedLanguage = languageCode;
      this.showLanguageMenu = false;
      await this.handleLanguageChange();
    },

    getFlagEmoji(languageCode) {
      const lang = this.languages.find(l => l.code === languageCode);
      return lang ? lang.flag : '🌐';
    },

    async handleLanguageChange() {
      localStorage.setItem('pageLanguage', this.selectedLanguage);

      if (this.selectedLanguage === 'fr') {
        this.restoreOriginalContent();
        this.restoreOriginalPlaceholders();
        this.showStatus('success');
        return;
      }

      await this.translateFullPage();
    },

    suspendObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.isObserverSuspended = true;
      }
    },

    resumeObserver() {
      if (this.observer && this.isObserverSuspended) {
        this.observer.observe(document.body, this.observerOptions);
        this.isObserverSuspended = false;
      }
    },

    storeOriginalContent() {
       const walker = document.createTreeWalker(
         document.body,
         NodeFilter.SHOW_TEXT,
         null,
         false
       );

       let node;
       while ((node = walker.nextNode())) {
         const text = node.textContent;
         if (text.trim().length > 0 &&
             !this.isInIgnoredElement(node) &&
             !this.isPriceOrNumber(text)) { // Add this check
           this.originalPageContent.push({
             node: node,
             originalText: text,
             parentElement: node.parentElement
           });
         }
       }

       console.log(`[PageTranslator] Stored ${this.originalPageContent.length} text nodes`);
       // Log if the sticky button text is captured
       const stickyButtonText = this.originalPageContent.find(item => item.originalText.includes('view'));
       console.log('[PageTranslator] Sticky button text captured?', stickyButtonText ? 'YES' : 'NO');
       if (!stickyButtonText) {
         console.log('[PageTranslator] All stored texts:', this.originalPageContent.map(item => item.originalText).slice(0, 20));
       }
     },

    isPriceOrNumber(text) {
      // Pattern for prices: €123, $123, 123€, 123$, 123.45€, etc.
      const pricePattern = /^[\d\s.,€$£¥₹]+$/;
      
      // Pattern for prices with currency symbols
      const currencyPattern = /[€$£¥₹]/;
      
      // Pattern for pure numbers with optional decimals
      const numberPattern = /^\d+([.,]\d+)?$/;
      
      // Check if text is primarily numbers/currency
      const hasOnlyCurrencyAndNumbers = pricePattern.test(text);
      const hasCurrency = currencyPattern.test(text);
      const isPureNumber = numberPattern.test(text);
      
      return hasOnlyCurrencyAndNumbers || (hasCurrency && text.length < 20) || isPureNumber;
    },

    storeOriginalPlaceholders() {
      // Store placeholders from input, textarea, and select elements
      const elements = document.querySelectorAll('input[placeholder], textarea[placeholder]');
      
      elements.forEach((element, index) => {
        const placeholder = element.getAttribute('placeholder');
        if (placeholder && placeholder.trim().length > 0 && !this.isInIgnoredElement(element)) {
          this.originalPlaceholders[index] = {
            element: element,
            originalPlaceholder: placeholder
          };
        }
      });
    },

    async translateFullPage() {
      this.isTranslating = true;
      this.translationStatus = null;
      this.translationProgress = null;

      try {
        // Collect text content
        const textsToTranslate = this.originalPageContent.map(
          (item) => item.originalText
        );

        // Collect placeholders
        const placeholdersToTranslate = Object.values(this.originalPlaceholders).map(
          (item) => item.originalPlaceholder
        );

        // Combine all texts to translate
        const allTexts = [...textsToTranslate, ...placeholdersToTranslate];

        if (allTexts.length === 0) {
          throw new Error('No content found to translate');
        }

        // OPTIMIZATION: Remove duplicates and create mapping
        const uniqueTexts = [...new Set(allTexts)];
        const translationCache = new Map();

        console.log(`Original texts: ${allTexts.length}, Unique texts: ${uniqueTexts.length}`);
        console.log(`Reduction: ${((1 - uniqueTexts.length / allTexts.length) * 100).toFixed(1)}%`);

        // Process unique texts sequentially
        const batchSize = 100;
        const totalBatches = Math.ceil(uniqueTexts.length / batchSize);

        for (let i = 0; i < uniqueTexts.length; i += batchSize) {
          const batch = uniqueTexts.slice(i, i + batchSize);
          const currentBatch = Math.floor(i / batchSize) + 1;

          this.translationProgress = `${currentBatch}/${totalBatches}`;

          try {
            const result = await this.translateBatch(batch);

            if (result.error) {
              throw new Error(result.error);
            }

            // Store translations in cache
            result.translations.forEach((translation, index) => {
              const originalText = batch[index];
              let translatedText = translation.translated_text || originalText;
              
              // Preserve whitespace from original text
              translatedText = this.preserveWhitespace(originalText, translatedText);
              
              translationCache.set(originalText, translatedText);
            });

          } catch (batchError) {
            console.error(`Batch ${currentBatch} failed:`, batchError);
            // Fallback: cache original text
            batch.forEach(text => {
              if (!translationCache.has(text)) {
                translationCache.set(text, text);
              }
            });
          }
        }

        // Map translations back to original order using cache
        const allTranslatedTexts = allTexts.map(text => 
          translationCache.get(text) || text
        );

        // Split translated texts back into content and placeholders
        const translatedContent = allTranslatedTexts.slice(0, textsToTranslate.length);
        const translatedPlaceholders = allTranslatedTexts.slice(textsToTranslate.length);

        // Apply translations
        this.suspendObserver();
        this.applyTranslations(translatedContent);
        this.applyPlaceholderTranslations(translatedPlaceholders);
        this.resumeObserver();

        this.showStatus('success');
      } catch (error) {
        console.error('Translation error:', error);
        this.showStatus('error');
      } finally {
        this.isTranslating = false;
        this.translationProgress = null;
      }
    },

    async translateBatch(texts) {
      try {
        const response = await fetch('/api/translate_batch', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCsrfToken()
          },
          body: JSON.stringify({
            texts: texts,
            target_language: this.selectedLanguage
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error('Batch translation error:', error);
        throw error;
      }
    },

    applyTranslations(translatedTexts) {
      const contentItems = this.originalPageContent;

      translatedTexts.forEach((translatedText, index) => {
        if (contentItems[index]) {
          const { node } = contentItems[index];
          if (node && node.parentElement) {
            node.textContent = translatedText;
          }
        }
      });
    },

    applyPlaceholderTranslations(translatedPlaceholders) {
      const placeholderItems = Object.values(this.originalPlaceholders);

      translatedPlaceholders.forEach((translatedPlaceholder, index) => {
        if (placeholderItems[index]) {
          const { element } = placeholderItems[index];
          if (element) {
            element.setAttribute('placeholder', translatedPlaceholder);
          }
        }
      });
    },

    restoreOriginalContent() {
      this.suspendObserver();
      this.originalPageContent.forEach(item => {
        const { node, originalText } = item;
        if (node && node.parentElement) {
          node.textContent = originalText;
        }
      });
      this.resumeObserver();
    },

    restoreOriginalPlaceholders() {
      this.suspendObserver();
      Object.values(this.originalPlaceholders).forEach(item => {
        const { element, originalPlaceholder } = item;
        if (element) {
          element.setAttribute('placeholder', originalPlaceholder);
        }
      });
      this.resumeObserver();
    },

    isInIgnoredElement(node) {
      const ignoredSelectors = [
        'script',
        'style',
        'noscript',
        '.language-selector',
        '.translator-header',
        '[data-no-translate]',
        '.no-translate'
      ];

      let current = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      
      while (current) {
        if (current.tagName === 'SCRIPT' || 
            current.tagName === 'STYLE' || 
            current.tagName === 'NOSCRIPT') {
          return true;
        }
        
        for (const selector of ignoredSelectors) {
          if (current.matches && current.matches(selector)) {
            return true;
          }
        }
        current = current.parentElement;
      }
      return false;
    },

    showStatus(status) {
      this.translationStatus = status;
      setTimeout(() => {
        this.translationStatus = null;
      }, 3000);
    },

    setupMutationObserver() {
       const observer = new MutationObserver((mutations) => {
         if (this.isObserverSuspended) return;

         let updatedTexts = [];
         let newTexts = [];
         let newPlaceholders = [];

         mutations.forEach((mutation) => {
           if (mutation.type === 'childList') {
             mutation.addedNodes.forEach((node) => {
               this.collectTranslatableFromNode(node, newTexts, newPlaceholders);
             });
           } else if (mutation.type === 'characterData') {
             // Handle text content changes
             const node = mutation.target;
             if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent;
                if (text.trim().length > 0 && !this.isInIgnoredElement(node) && !this.isPriceOrNumber(text)) {
                   // Check if we already have this node
                   const existing = this.originalPageContent.find(i => i.node === node);
                   if (!existing) {
                      newTexts.push({
                        node: node,
                        originalText: text,
                        parentElement: node.parentElement
                      });
                   } else if (existing.originalText !== text) {
                      // Text changed. Update source.
                      existing.originalText = text;
                      updatedTexts.push(existing);
                   }
                }
             }
           }
         });

         // Filter newTexts definitions to avoid duplicates if nodes were moved or re-added
         const trulyNewTexts = [];
         newTexts.forEach(item => {
            const existing = this.originalPageContent.find(i => i.node === item.node);
            if (existing) {
                // Node exists. Did text change?
                if (existing.originalText !== item.originalText) {
                    existing.originalText = item.originalText;
                    updatedTexts.push(existing);
                }
            } else {
                trulyNewTexts.push(item);
            }
         });

         if (trulyNewTexts.length > 0 || updatedTexts.length > 0 || newPlaceholders.length > 0) {
           console.log('[PageTranslator] New/Updated content detected:', {
             newTextsCount: trulyNewTexts.length,
             updatedTextsCount: updatedTexts.length,
             newPlaceholdersCount: newPlaceholders.length,
             currentLanguage: this.selectedLanguage
           });
           
           // Add to originals
           trulyNewTexts.forEach((item) => this.originalPageContent.push(item));
           
           const startIndex = Object.keys(this.originalPlaceholders).length;
           newPlaceholders.forEach((item, idx) => {
             this.originalPlaceholders[startIndex + idx] = item;
           });
           
           if (this.selectedLanguage !== 'fr') {
             this.translateNewContent([...trulyNewTexts, ...updatedTexts], newPlaceholders);
           }
         }
       });
      this.observer = observer;
      // Start observing
      observer.observe(document.body, this.observerOptions);
    },

    collectTranslatableFromNode(node, texts, placeholders) {
       if (node.nodeType === Node.TEXT_NODE) {
         const text = node.textContent;
         if (text.trim().length > 0 && !this.isInIgnoredElement(node) && !this.isPriceOrNumber(text)) {
           texts.push({
             node: node,
             originalText: text,
             parentElement: node.parentElement
           });
           // Log if this is the sticky button text
           if (text.includes('view')) {
             console.log('[PageTranslator] Collected sticky button text:', text, 'Parent:', node.parentElement?.className);
           }
         }
       } else if (node.nodeType === Node.ELEMENT_NODE) {
         if (!this.isInIgnoredElement(node)) {
           if (node.matches && node.matches('input[placeholder], textarea[placeholder]')) {
             const placeholder = node.getAttribute('placeholder');
             if (placeholder && placeholder.trim().length > 0) {
               placeholders.push({
                 element: node,
                 originalPlaceholder: placeholder
               });
             }
           }
           // Recurse on children
           node.childNodes.forEach((child) => this.collectTranslatableFromNode(child, texts, placeholders));
         }
       }
     },

    async translateNewContent(newTexts, newPlaceholders) {
       const textsToTranslate = newTexts.map((i) => i.originalText);
       const placeholdersToTranslate = newPlaceholders.map((i) => i.originalPlaceholder);
       const allTexts = [...textsToTranslate, ...placeholdersToTranslate];
       if (allTexts.length === 0) return;
       try {
         console.log('[PageTranslator] Translating new content:', textsToTranslate);
         const result = await this.translateBatch(allTexts);
         if (result.error) {
           console.error('[PageTranslator] Translation error:', result.error);
           return;
         }
          const translations = result.translations;
          console.log('[PageTranslator] Translations received:', translations);
          
          this.suspendObserver();
          // Apply to texts
          translations.slice(0, textsToTranslate.length).forEach((trans, idx) => {
            let translatedText = trans.translated_text || trans;
            
            // Preserve whitespace
            translatedText = this.preserveWhitespace(textsToTranslate[idx], translatedText);

            console.log('[PageTranslator] Applying translation:', textsToTranslate[idx], '->', translatedText);
            newTexts[idx].node.textContent = translatedText;
          });
          // Apply to placeholders
          translations.slice(textsToTranslate.length).forEach((trans, idx) => {
            newPlaceholders[idx].element.setAttribute('placeholder', trans.translated_text || trans);
          });
          this.resumeObserver();
       } catch (error) {
         console.error('Error translating new content:', error);
       }
     },

    getCsrfToken() {
      const token = document.querySelector('meta[name="csrf-token"]');
      return token ? token.getAttribute('content') : '';
    },

    preserveWhitespace(original, translated) {
      if (!original || !translated) return translated;
      
      const leadingSpace = original.match(/^\s*/)[0];
      const trailingSpace = original.match(/\s*$/)[0];
      
      return leadingSpace + translated.trim() + trailingSpace;
    }
  }
};
</script>

<style scoped>
.page-translator-container {
  position: sticky;
  top: 0;
  z-index: 102;
}

.translator-header {
  display: flex;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.language-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.language-selector.status-success {
  animation: successPulse 0.5s ease-in-out;
}

.language-selector.status-error {
  animation: errorShake 0.5s ease-in-out;
}

@keyframes successPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes errorShake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.custom-language-selector {
  position: relative;
  display: inline-block;
}

.language-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.8rem;
  border: 1px solid #000;
  border-radius: 6px;
  color: #000;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  min-width: 70px;
  justify-content: center;

  @media (max-width: 768px) {
    color: #fff;
    border: 1px solid #fff;
  }
}

.language-button:hover {
  border-color: #3B6152;
  box-shadow: 0 2px 8px rgba(59, 97, 82, 0.15);
}

.language-button:focus {
  outline: none;
  border-color: #3B6152;
  box-shadow: 0 0 0 3px rgba(59, 97, 82, 0.2);
}

.language-code {
  font-size: 0.85rem;
}

.language-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.5rem;
  background: white;
  border: 1px solid #000;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 100px;
  overflow: hidden;
}

.language-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  color: #333;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.language-option:hover {
  background-color: #f5f5f5;
}

.language-option.active {
  background-color: #e8f5e9;
  color: #3B6152;
  font-weight: 600;
}

.language-option:not(:last-child) {
  border-bottom: 1px solid #eee;
}

.language-name {
  flex: 1;
}

.status-success .language-button {
  border-color: #3B6152;
  box-shadow: 0 0 0 3px rgba(59, 97, 82, 0.15);
}

.status-error .language-button {
  border-color: #f44336;
  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.15);
}

.translation-status {
  display: flex;
  align-items: center;
}

.spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3B6152;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .page-translator-container {
    padding: 0rem;
    margin-left: 2px;
  }

  .language-button {
    min-width: 35px;
    padding: 0.5rem 0.5rem;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .translator-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .language-button {
    width: auto;
  }

  .language-menu {
    position: absolute;
    left: 0;
    right: auto;
    top: 100%;
    bottom: auto;
    border-radius: 6px;
    max-height: none;
    overflow-y: visible;
    min-width: 60px;
  }
  .language-option {
    padding: 5px;
    gap: 2px;
  }
}
</style>